import { User } from 'oidc-react';

import { cloudApiConfig } from '../config';
import type { ClusterParams, ClusterStatus, PaymentIntent, ClusterApp } from '../types/cluster';

export const apiRoot = `${cloudApiConfig.apiRoot()}/api/v1`;
export const clusterRoot = `${apiRoot}/clouds`;
const ingressFirstByName = (a: ClusterApp, b: ClusterApp) => {
  // Check if a.href or a.hrefs exists and is non-empty
  const aHasHref = a?.status?.href?.length || a?.status?.hrefs?.length;
  // Check if b.href or b.hrefs exists and is non-empty
  const bHasHref = b?.status?.href?.length || b?.status?.hrefs?.length;

  // If only one has href or hrefs, prioritize that one
  if (aHasHref && !bHasHref) {
    return -1;
  }
  if (!aHasHref && bHasHref) {
    return 1;
  }

  // If both have or both do not have href or hrefs, compare by name
  const nameA = a.name.toUpperCase();
  const nameB = b.name.toUpperCase();

  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }

  return 0;
};

interface CFRedirect {
  redirect: string;
}

export interface AWSDetail {
  id: string;
  microARN: string;
  role: string;
  canAssume: boolean;
}

export interface Profile {
  stripe: boolean;
  aws: AWSDetail;
  user: User;
}

export const clusterApi = (user: User) => {
  const opts = () => ({
    headers: {
      Authorization: `Bearer ${user?.access_token}`,
    },
  });

  return {
    async saveStripeIntent(intent: string): Promise<void> {
      await fetch(`${apiRoot}/payments`, { ...opts(), method: 'POST', body: JSON.stringify({intent}) });
    },

    async checkExists(c: string): Promise<boolean> {
      const res = await fetch(`${clusterRoot}/${c}/exists`, { ...opts() });
      return res.status === 409;
    },

    async getStripeIntent(): Promise<PaymentIntent> {
      try {
        const res = await fetch(`${apiRoot}/payments`, { ...opts() });
        if (!res.ok) {
          throw new Error(`Failed to get payment intent: ${res.status}`);
        }
        
        // Handle non-JSON responses
        const text = await res.text();
        try {
          return JSON.parse(text) as PaymentIntent;
        } catch (parseError) {
          console.error('JSON parse error in getStripeIntent:', parseError);
          throw new Error('Invalid response format from payment service');
        }
      } catch (error) {
        console.error('Error in getStripeIntent:', error);
        throw error;
      }
    },

    async getProfile(): Promise<Profile> {
      try {
        const res = await fetch(`${apiRoot}/me`, { ...opts() });
        
        // Handle non-JSON responses
        const text = await res.text();
        try {
          return JSON.parse(text) as Profile;
        } catch (parseError) {
          console.error('JSON parse error in getProfile:', parseError);
          // Return a default profile structure instead of throwing
          return {
            stripe: false,
            aws: { id: '', microARN: '', role: '', canAssume: false },
            user: user
          };
        }
      } catch (error) {
        console.error('Network error in getProfile:', error);
        // Return a default profile structure instead of throwing
        return {
          stripe: false,
          aws: { id: '', microARN: '', role: '', canAssume: false },
          user: user
        };
      }
    },

    async getCloudformation(id: string): Promise<string> {
      try {
        const res = await fetch(`${clusterRoot}/aws/cloudformation`, {
          method: 'POST',
          body: JSON.stringify({accountID: id}),
          ...opts(),
        });
        
        if (!res.ok) {
          throw new Error(`Failed to get CloudFormation: ${res.status}`);
        }
        
        // Handle non-JSON responses
        const text = await res.text();
        try {
          const j = JSON.parse(text) as CFRedirect;
          return j.redirect;
        } catch (parseError) {
          console.error('JSON parse error in getCloudformation:', parseError);
          throw new Error('Invalid response format from CloudFormation service');
        }
      } catch (error) {
        console.error('Error in getCloudformation:', error);
        throw error;
      }
    },

    async listDNS(): Promise<string[]> {
      try {
        const res = await fetch(`${clusterRoot}/dns`, opts());
        
        if (!res.ok) {
          throw new Error(`Failed to list DNS: ${res.status}`);
        }
        
        // Handle non-JSON responses
        const text = await res.text();
        try {
          return JSON.parse(text) as string[];
        } catch (parseError) {
          console.error('JSON parse error in listDNS:', parseError);
          // Return an empty array instead of throwing
          return [];
        }
      } catch (error) {
        console.error('Error in listDNS:', error);
        // Return an empty array for any network errors
        return [];
      }
    },

    async listClusters(): Promise<ClusterStatus[]> {
      try {
        const res = await fetch(`${clusterRoot}/`, { ...opts() });
        
        if (!res.ok) {
          throw new Error(`Failed to list clusters: ${res.status}`);
        }
        
        // Handle non-JSON responses
        const text = await res.text();
        try {
          return JSON.parse(text) as ClusterStatus[];
        } catch (parseError) {
          console.error('JSON parse error in listClusters:', parseError);
          // Return an empty array instead of throwing
          return [];
        }
      } catch (error) {
        console.error('Error in listClusters:', error);
        // Return an empty array for any network errors
        return [];
      }
    },

    async refreshCluster(name: string): Promise<void> {
      try {
        const res = await fetch(`${clusterRoot}/${name}/refresh`, { ...opts(), method: 'POST'});
        
        if (!res.ok) {
          const errorText = await res.text();
          try {
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || `Failed to refresh cluster ${name}: ${res.status}`);
          } catch (parseError) {
            throw new Error(`Failed to refresh cluster ${name}: ${res.status}. ${errorText.substring(0, 100)}`);
          }
        }
      } catch (error) {
        console.error(`Error refreshing cluster ${name}:`, error);
        throw error;
      }
    },

    async getCluster(name: string): Promise<ClusterStatus> {
      try {
        const res = await fetch(`${clusterRoot}/${name}`, { ...opts() });
        
        if (!res.ok) {
          throw new Error(`Failed to get cluster ${name}: ${res.status}`);
        }
        
        // Handle non-JSON responses
        const text = await res.text();
        try {
          return JSON.parse(text) as ClusterStatus;
        } catch (parseError) {
          console.error(`JSON parse error in getCluster for ${name}:`, parseError);
          throw new Error(`Invalid response format for cluster ${name}`);
        }
      } catch (error) {
        console.error(`Error in getCluster for ${name}:`, error);
        throw error;
      }
    },

    async getClusterApps(name: string): Promise<ClusterApp[]> {
      try {
        const res = await fetch(`${clusterRoot}/${name}/apps`, { ...opts() });
        
        if (!res.ok) {
          throw new Error(`Failed to get apps for cluster ${name}: ${res.status}`);
        }
        
        // Handle non-JSON responses
        const text = await res.text();
        try {
          const apps = JSON.parse(text) as ClusterApp[];
          apps.sort(ingressFirstByName);
          return apps;
        } catch (parseError) {
          console.error(`JSON parse error in getClusterApps for ${name}:`, parseError);
          // Return an empty array instead of throwing
          return [];
        }
      } catch (error) {
        console.error(`Error in getClusterApps for ${name}:`, error);
        // Return an empty array for any network errors
        return [];
      }
    },

    async createCluster(s: ClusterParams): Promise<ClusterParams> {
      try {
        const {
          name,
          ...body
        } = s;
        const res = await fetch(`${clusterRoot}/${name}`, {
          method: 'PUT',
          headers: {
            ...opts().headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          try {
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || `Failed to create cluster: ${res.status}`);
          } catch (parseError) {
            throw new Error(`Failed to create cluster: ${res.status}. ${errorText.substring(0, 100)}`);
          }
        }
        
        // Handle non-JSON responses
        const text = await res.text();
        try {
          return JSON.parse(text) as ClusterParams;
        } catch (parseError) {
          console.error('JSON parse error in createCluster:', parseError);
          // Return the original params as fallback
          return s;
        }
      } catch (error) {
        console.error('Error in createCluster:', error);
        throw error;
      }
    },

    async deleteCluster(s: string): Promise<void> {
      try {
        const res = await fetch(`${clusterRoot}/${s}`, {
          ...opts(),
          method: 'DELETE',
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          try {
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || `Failed to delete cluster ${s}: ${res.status}`);
          } catch (parseError) {
            throw new Error(`Failed to delete cluster ${s}: ${res.status}. ${errorText.substring(0, 100)}`);
          }
        }
      } catch (error) {
        console.error(`Error deleting cluster ${s}:`, error);
        throw error;
      }
    },

    async getKubeconfig(cluster: string, openid: boolean): Promise<string> {
      try {
        const res = await fetch(`${clusterRoot}/${cluster}/kubeconfig?oidc=${openid === true}`, { ...opts() });
        
        if (!res.ok) {
          const errorText = await res.text();
          try {
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || `Failed to get kubeconfig for ${cluster}: ${res.status}`);
          } catch (parseError) {
            throw new Error(`Failed to get kubeconfig for ${cluster}: ${res.status}. ${errorText.substring(0, 100)}`);
          }
        }
        
        return await res.text();
      } catch (error) {
        console.error(`Error getting kubeconfig for ${cluster}:`, error);
        throw error;
      }
    },

    async saveAddress(addr: string): Promise<void> {
    }
  };
};
